// The one table of the admin app (wireframes style guide): sortable columns, keyset "Load more"
// pagination, the CSV download link where the API offers one, and the shared states in place of
// rows. Money cells render from integer µUSD with the exact value in the tooltip. A table whose
// rows open a summary (RowDetail) makes each row clickable instead of trailing action buttons.
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { ApiError } from "../api/errors";
import { HelpButton, type HelpId } from "./help";
import { money, type MicroUsd } from "./money";
import { ROW_OPEN_CLASS, rowClickHandler } from "./row-click";
import { Empty, ErrorBanner, Loading } from "./states";

export interface Column<T> {
  key: string;
  header: string;
  cell(row: T): ComponentChildren;
  /** Present when the column sorts; compares two rows ascending. */
  compare?(a: T, b: T): number;
  numeric?: boolean | undefined;
  /** A "?" beside a header whose meaning is not obvious (Cost basis, Drift, …). */
  help?: HelpId | undefined;
}

export interface DataTableProps<T> {
  caption: string;
  /** Hide the caption visually (a card heading already names the table). */
  hideCaption?: boolean | undefined;
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey(row: T): string;
  loading?: boolean | undefined;
  error?: ApiError | null | undefined;
  onRetry?: (() => void) | undefined;
  emptyMessage: string;
  emptyAction?: ComponentChildren | undefined;
  /** Keyset pagination: the next page exists while this is non-null. */
  nextCursor?: string | null | undefined;
  onLoadMore?: (() => void) | undefined;
  csvHref?: string | undefined;
  /**
   * Makes each row open its summary (a RowDetail): a click anywhere on the row, or Enter/Space on
   * the row's own button (visually hidden in the first cell, named by `rowLabel`; see
   * ui/row-click.ts). A click on a cell's own link, button or control is left to that control and
   * does not open the row, so a cell renderer that holds one needs no stopPropagation.
   */
  onRowClick?: ((row: T) => void) | undefined;
  /** The accessible name of a row's open button ("Open the session for 123…"). */
  rowLabel?: ((row: T) => string) | undefined;
  /**
   * The table may be wider than its box (a phone) and has no control of its own to reach the
   * scroll with: the scroll box takes focus, as a region named by the caption, so the keyboard
   * scrolls it (axe `scrollable-region-focusable`). Opt-in: a region per table would duplicate
   * the name of the card around most of them.
   */
  keyboardScroll?: boolean | undefined;
}

type SortState = { key: string; direction: "ascending" | "descending" } | null;

function sorted<T>(rows: readonly T[], columns: readonly Column<T>[], sort: SortState): readonly T[] {
  const column = sort && columns.find((c) => c.key === sort.key);
  if (!sort || !column?.compare) return rows;
  const compare = column.compare;
  const sign = sort.direction === "ascending" ? 1 : -1;
  return [...rows].sort((a, b) => sign * compare(a, b));
}

export function DataTable<T>(props: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(null);
  const toggle = (key: string) =>
    setSort((current) =>
      current?.key === key && current.direction === "ascending" ? { key, direction: "descending" } : { key, direction: "ascending" },
    );

  if (props.error) return <ErrorBanner error={props.error} {...(props.onRetry ? { onRetry: props.onRetry } : {})} />;
  if (props.loading && props.rows.length === 0) return <Loading />;
  if (props.rows.length === 0) return <Empty message={props.emptyMessage} action={props.emptyAction} />;

  return (
    <div class="table-block">
      <div class="table-scroll" {...(props.keyboardScroll ? { tabIndex: 0, role: "region", "aria-label": props.caption } : {})}>
        <table class="data-table">
          <caption class={props.hideCaption ? "visually-hidden" : undefined}>{props.caption}</caption>
          <thead>
            <tr>
              {props.columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  class={column.numeric ? "numeric" : undefined}
                  aria-sort={sort?.key === column.key ? sort.direction : undefined}
                >
                  <span class="th-inner">
                    {column.compare ? (
                      <button type="button" class="sort-button" onClick={() => toggle(column.key)}>
                        {column.header}
                        <span aria-hidden="true" class="sort-mark">
                          {sort?.key === column.key ? (sort.direction === "ascending" ? " ▲" : " ▼") : ""}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                    {column.help && <HelpButton topic={column.help} label={column.header} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted(props.rows, props.columns, sort).map((row) => {
              const cells = props.columns.map((column, index) => (
                <td key={column.key} class={column.numeric ? "numeric" : undefined}>
                  {index === 0 && props.onRowClick && (
                    <button type="button" class={ROW_OPEN_CLASS} aria-label={props.rowLabel?.(row) ?? "Show details"} />
                  )}
                  {column.cell(row)}
                </td>
              ));
              const onRowClick = props.onRowClick;
              return onRowClick ? (
                <tr key={props.rowKey(row)} class="row-clickable" onClick={rowClickHandler(() => onRowClick(row))}>
                  {cells}
                </tr>
              ) : (
                <tr key={props.rowKey(row)}>{cells}</tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        {props.nextCursor && props.onLoadMore && (
          <button type="button" class="button" onClick={props.onLoadMore} disabled={props.loading}>
            {props.loading ? "Loading…" : "Load more"}
          </button>
        )}
        {props.csvHref && (
          <a class="button button-secondary" href={props.csvHref} download>
            Download CSV
          </a>
        )}
      </div>
    </div>
  );
}

/** A money cell: dollars to the cent, the exact µUSD-derived value on hover and for screen readers. */
export function Money({ micro }: { micro: MicroUsd }) {
  const display = money(micro);
  return (
    <span class={display.negative ? "money money-negative" : "money"} title={display.exact} aria-label={display.exact}>
      {display.text}
    </span>
  );
}
